import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api.js';
import { MEAL_PERIOD_FILTER_OPTIONS } from '../../../utils/mealPeriods.js';

// UC 34266 — Brand Cost Allocation Report (Finance / SuperAdmin)

/* ─── Formatters ──────────────────────────────────────────── */
function currency(n, symbol = '₱') {
  if (n == null) return '—';
  const v = Number(n);
  return `${symbol}${Math.abs(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function currencySigned(n) {
  if (n == null) return '—';
  const v = Number(n);
  return `${v < 0 ? '-' : ''}₱${Math.abs(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function pctSigned(n) {
  if (n == null) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}
function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function niceMax(val) {
  if (val <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(val)));
  const steps = [1, 2, 2.5, 5, 10];
  for (const s of steps) if (s * mag >= val) return s * mag;
  return val;
}
function axisTicks(max, count = 5) {
  const step = niceMax(max / (count - 1));
  return Array.from({ length: count }, (_, i) => i * step);
}
function fmtCostTick(v) {
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
  return String(v);
}
function fmtVarTick(v) {
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
  return String(v);
}

/* ─── Bar + Line Dual-Axis Chart ─────────────────────────── */
function BarLineChart({ brands }) {
  if (!brands || brands.length === 0) return null;

  // Dynamic width — 60px per brand group minimum so bars never get crushed
  const GROUP_W = 60;
  const pad = { top: 20, right: 70, bottom: 80, left: 72 };
  const iH = 220;
  const iW = Math.max(500, brands.length * GROUP_W);
  const W = iW + pad.left + pad.right;
  const H = iH + pad.top + pad.bottom;

  const maxCost = Math.max(...brands.map((b) => Math.max(b.total_standard_cost || 0, b.total_actual_cost || 0)), 1);
  const costMax = niceMax(maxCost * 1.15);
  const costTicks = axisTicks(costMax, 6);

  const varValues = brands.map((b) => b.total_variance || 0);
  const absMaxVar = Math.max(...varValues.map(Math.abs), 1);
  const varHalf = niceMax(absMaxVar * 1.4);
  const varTicks = [-2, -1, 0, 1, 2].map((t) => (t * varHalf) / 2);

  const barW = Math.min(22, GROUP_W * 0.34);

  function costY(v) { return pad.top + iH - (v / costMax) * iH; }
  function varY(v)  { return pad.top + iH / 2 - (v / varHalf) * (iH / 2); }
  function gx(i)    { return pad.left + i * GROUP_W + GROUP_W / 2; }

  const linePts = brands.map((b, i) => `${gx(i)},${varY(b.total_variance || 0)}`);
  const linePath = `M ${linePts.join(' L ')}`;

  return (
    // Horizontal scroll wrapper — axis labels stay fixed via the fixed left/right padding
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', minWidth: W, height: H }}
        width={W}
        height={H}
      >
        {/* Left axis label */}
        <text
          transform={`translate(14, ${pad.top + iH / 2}) rotate(-90)`}
          textAnchor="middle" fontSize="10" fill="#9ca3af"
        >Cost (₱)</text>

        {/* Right axis label */}
        <text
          transform={`translate(${W - 14}, ${pad.top + iH / 2}) rotate(90)`}
          textAnchor="middle" fontSize="10" fill="#9ca3af"
        >Variance (₱)</text>

        {/* Grid lines + left tick labels */}
        {costTicks.map((t) => {
          const y = costY(t);
          return (
            <g key={t}>
              <line x1={pad.left} y1={y} x2={pad.left + iW} y2={y} stroke="#e5e7eb" strokeWidth="1" />
              <text x={pad.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">
                {fmtCostTick(t)}
              </text>
            </g>
          );
        })}

        {/* Right axis variance ticks */}
        {varTicks.map((t) => {
          const y = varY(t);
          return (
            <text key={t} x={pad.left + iW + 8} y={y + 4} textAnchor="start" fontSize="10" fill="#9ca3af">
              {fmtVarTick(t)}
            </text>
          );
        })}

        {/* Zero-variance dashed line */}
        <line x1={pad.left} y1={varY(0)} x2={pad.left + iW} y2={varY(0)}
          stroke="#d1d5db" strokeWidth="1" strokeDasharray="4 3" />

        {/* X-axis baseline */}
        <line x1={pad.left} y1={pad.top + iH} x2={pad.left + iW} y2={pad.top + iH}
          stroke="#d1d5db" strokeWidth="1" />

        {/* Bars + rotated x labels */}
        {brands.map((b, i) => {
          const cx = gx(i);
          const sh = Math.max(2, ((b.total_standard_cost || 0) / costMax) * iH);
          const ah = Math.max(2, ((b.total_actual_cost  || 0) / costMax) * iH);
          const label = b.brand_name.length > 14 ? b.brand_name.slice(0, 13) + '…' : b.brand_name;
          return (
            <g key={b.brand_id}>
              <rect x={cx - barW - 1} y={pad.top + iH - sh} width={barW} height={sh} fill="#008080" rx="2" />
              <rect x={cx + 1}        y={pad.top + iH - ah} width={barW} height={ah} fill="#d97706" rx="2" />
              {/* Rotated label — avoids overlap regardless of count */}
              <text
                transform={`translate(${cx}, ${pad.top + iH + 10}) rotate(-40)`}
                textAnchor="end" fontSize="10" fill="#374151"
              >{label}</text>
            </g>
          );
        })}

        {/* Variance line + dots */}
        <path d={linePath} fill="none" stroke="#ef4444" strokeWidth="2" strokeLinejoin="round" />
      {brands.map((b, i) => (
        <circle key={b.brand_id} cx={gx(i)} cy={varY(b.total_variance || 0)} r="4" fill="#ef4444" />
      ))}

      </svg>
    </div>
  );
}

/* ─── Donut Chart ────────────────────────────────────────── */
function DonutChart({ brands }) {
  if (!brands || brands.length === 0) return null;

  const over  = brands.filter((b) => b.total_variance > 0).length;
  const under = brands.filter((b) => b.total_variance < 0).length;
  const on    = brands.length - over - under;
  const total = brands.length;

  // cx/cy is center of the donut; R = outer radius, r = inner hole radius
  const cx = 110, cy = 110, R = 88, r = 52;

  const rawSegments = [
    { label: 'Over Target',  count: over,  color: '#ef4444' },
    { label: 'Under Target', count: under, color: '#22c55e' },
    { label: 'On Target',    count: on,    color: '#3b82f6' },
  ].filter((s) => s.count > 0);

  let cumAngle = -Math.PI / 2;
  const slices = rawSegments.map((seg) => {
    const pct = seg.count / total;
    const angle = pct * 2 * Math.PI;
    const startA = cumAngle;
    const endA = startA + angle;
    cumAngle = endA;

    const large = angle > Math.PI ? 1 : 0;
    const x1 = cx + R * Math.cos(startA), y1 = cy + R * Math.sin(startA);
    const x2 = cx + R * Math.cos(endA),   y2 = cy + R * Math.sin(endA);

    // Label position: midpoint of arc at 72% of R (inside the band)
    const midA = startA + angle / 2;
    const labelR = (R + r) / 2;
    const lx = cx + labelR * Math.cos(midA);
    const ly = cy + labelR * Math.sin(midA);

    return {
      ...seg, pct,
      path: `M${cx},${cy} L${x1},${y1} A${R},${R} 0 ${large},1 ${x2},${y2} Z`,
      lx, ly,
      pctLabel: `${(pct * 100).toFixed(1)}%`,
    };
  });

  const SIZE = cx * 2;

  return (
    <div className="d-flex flex-column align-items-center">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ width: 200, height: 200 }}>
        {slices.map((s) => (
          <g key={s.label}>
            <path d={s.path} fill={s.color} />
          </g>
        ))}
        {/* White center hole */}
        <circle cx={cx} cy={cy} r={r} fill="#fff" />
        {/* Labels drawn on top of everything */}
        {slices.map((s) => (
          <g key={`lbl-${s.label}`}>
            <text x={s.lx} y={s.ly - 6} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">
              {s.label}
            </text>
            <text x={s.lx} y={s.ly + 7} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">
              {s.pctLabel}
            </text>
          </g>
        ))}
      </svg>

      {/* Legend */}
      <div className="d-flex justify-content-center flex-wrap mt-2" style={{ gap: '8px 16px' }}>
        {[
          { label: 'Over Target',  color: '#ef4444' },
          { label: 'Under Target', color: '#22c55e' },
          { label: 'On Target',    color: '#3b82f6' },
        ].map((item) => (
          <div key={item.label} className="d-flex align-items-center" style={{ gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.78rem', color: '#374151' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────── */
export default function BrandCostAllocationReportPage() {
  const today    = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const monthAhd = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  const [dateStart, setDateStart] = useState(monthAgo);
  const [dateEnd,   setDateEnd]   = useState(monthAhd);
  const [allBrands, setAllBrands] = useState([]);
  const [allItems,  setAllItems]  = useState([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);
  const [selectedItemIds,  setSelectedItemIds]  = useState([]);
  const [costBasis, setCostBasis] = useState('actual_vs_standard');
  const [mealPeriod, setMealPeriod] = useState('ALL');
  const [report,   setReport]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  // Table controls
  const [expandedBrand,  setExpandedBrand]  = useState(null);
  const [varFilter,      setVarFilter]      = useState('all');   // all | over | under | on
  const [tableSearch,    setTableSearch]    = useState('');
  const [page,           setPage]           = useState(1);
  const PAGE_SIZE = 10;

  // Applied filter snapshot (for "Reporting Period" column)
  const [appliedStart, setAppliedStart] = useState(monthAgo);
  const [appliedEnd,   setAppliedEnd]   = useState(monthAhd);

  const filterRef = useRef({ dateStart, dateEnd, selectedBrandIds, mealPeriod });
  useEffect(() => { filterRef.current = { dateStart, dateEnd, selectedBrandIds, mealPeriod }; });

  useEffect(() => {
    Promise.all([
      api.get('/master-data/brands').then((r) => r.data.data || []).catch(() => []),
      api.get('/master-data/items').then((r) => r.data.data || []).catch(() => []),
    ]).then(([b, it]) => { setAllBrands(b); setAllItems(it); });
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    const { dateStart: ds, dateEnd: de, selectedBrandIds: ids, mealPeriod: mp } = filterRef.current;
    try {
      const params = new URLSearchParams();
      if (ds) params.set('date_start', ds);
      if (de) params.set('date_end', de);
      if (ids.length > 0) params.set('brand_ids', ids.join(','));
      if (mp && mp !== 'ALL') params.set('meal_period', mp);
      const res = await api.get(`/brand-cost-report?${params}`);
      setReport(res.data.data);
      setAppliedStart(ds);
      setAppliedEnd(de);
      setPage(1);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  function resetFilters() {
    setSelectedBrandIds([]); setSelectedItemIds([]);
    setDateStart(monthAgo); setDateEnd(monthAhd);
    setCostBasis('actual_vs_standard');
  }

  function exportCsv(brandRow) {
    const rows = [['Brand', 'Reporting Period', 'Prod Qty', 'Standard Cost', 'Actual Cost', 'Variance', 'Variance %']];
    const src = brandRow ? [brandRow] : (report?.brands || []);
    src.forEach((b) => rows.push([
      b.brand_name,
      `${fmtDate(appliedStart)} - ${fmtDate(appliedEnd)}`,
      b.production_qty, b.total_standard_cost, b.total_actual_cost, b.total_variance,
      b.variance_pct != null ? `${b.variance_pct}%` : 'N/A',
    ]));
    const csv = rows.map((r) => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `brand-cost-${appliedStart}-${appliedEnd}.csv`,
    });
    a.click();
  }

  const brands = report?.brands || [];

  // Aggregate KPIs
  const totalStd = brands.reduce((s, b) => s + (b.total_standard_cost || 0), 0);
  const totalAct = brands.reduce((s, b) => s + (b.total_actual_cost  || 0), 0);
  const totalVar = brands.reduce((s, b) => s + (b.total_variance     || 0), 0);
  const totalQty = brands.reduce((s, b) => s + (b.production_qty     || 0), 0);
  const varPct   = totalStd > 0 ? (totalVar / totalStd) * 100 : 0;

  // Table filtering
  const filteredBrands = brands.filter((b) => {
    if (varFilter === 'over'  && b.total_variance <= 0) return false;
    if (varFilter === 'under' && b.total_variance >= 0) return false;
    if (varFilter === 'on'    && b.total_variance !== 0) return false;
    if (tableSearch) {
      const q = tableSearch.toLowerCase();
      if (!(b.brand_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filteredBrands.length / PAGE_SIZE));
  const pageBrands = filteredBrands.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const drilldownRuns = expandedBrand && report ? (report.runs || []).filter((r) => r.brand_id === expandedBrand) : [];

  function variancePill(variance, pctVal) {
    const style = variance > 0
      ? { background: '#fee2e2', color: '#991b1b' }
      : variance < 0
        ? { background: '#d1fae5', color: '#065f46' }
        : { background: '#e5e7eb', color: '#374151' };
    return (
      <span style={{ ...style, borderRadius: 20, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700, display: 'inline-block' }}>
        {pctSigned(pctVal)}
      </span>
    );
  }

  const LINK = { color: '#008080', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', background: 'none', border: 'none', padding: 0 };

  return (
    <div className="container-fluid py-4" data-testid="brand-cost-report-page">

      {/* ── Header ── */}
      <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap" style={{ gap: 12 }}>
        <div>
          <div className="text-muted small mb-1">
            <span style={{ color: '#008080', cursor: 'pointer' }}>Recipe Repository</span>
            <span className="mx-1">/</span>Brand Cost Allocation Report
          </div>
          <h2 className="h4 font-weight-bold mb-1">Brand Cost Allocation Report</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Review actual vs standard production cost by brand and export finance-ready summaries.
          </p>
        </div>
        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetFilters} data-testid="btn-reset">
            <i className="fa-solid fa-rotate-left mr-1" />Reset
          </button>
          <button type="button" className="btn btn-sm btn-warning font-weight-bold" onClick={() => exportCsv(null)}
            disabled={!report || brands.length === 0} data-testid="btn-export-line">
            <i className="fa-solid fa-file-lines mr-1" />Export Line Detail
          </button>
          <button type="button" className="btn btn-sm font-weight-bold"
            style={{ background: '#008080', color: '#fff', borderColor: '#008080' }}
            onClick={() => exportCsv(null)} disabled={!report || brands.length === 0} data-testid="btn-export-excel">
            <i className="fa-solid fa-file-excel mr-1" />Export to Excel
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="card shadow-sm mb-4 border-0" data-testid="filter-panel">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-2 mb-2">
              <label className="small font-weight-bold">Start Date <span className="text-danger">*</span></label>
              <input type="date" className="form-control form-control-sm" value={dateStart}
                onChange={(e) => setDateStart(e.target.value)} data-testid="filter-date-start" />
            </div>
            <div className="col-md-2 mb-2">
              <label className="small font-weight-bold">End Date <span className="text-danger">*</span></label>
              <input type="date" className="form-control form-control-sm" value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)} data-testid="filter-date-end" />
            </div>
            <div className="col-md-3 mb-2">
              <label className="small font-weight-bold">Brand <span className="text-danger">*</span></label>
              <select className="form-control form-control-sm" multiple size={4}
                value={selectedBrandIds}
                onChange={(e) => setSelectedBrandIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
                data-testid="filter-brand-select">
                {allBrands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="col-md-3 mb-2">
              <label className="small font-weight-bold">Recipe / SKU</label>
              <select className="form-control form-control-sm" multiple size={4}
                value={selectedItemIds}
                onChange={(e) => setSelectedItemIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
                data-testid="filter-item-select">
                {allItems.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
              </select>
            </div>
            <div className="col-md-2 mb-2">
              <label className="small font-weight-bold">Meal Period</label>
              <select className="form-control form-control-sm mb-2" value={mealPeriod}
                onChange={(e) => setMealPeriod(e.target.value)} data-testid="filter-meal-period">
                {MEAL_PERIOD_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2 mb-2">
              <label className="small font-weight-bold">Cost Basis <span className="text-danger">*</span></label>
              <select className="form-control form-control-sm mb-2" value={costBasis}
                onChange={(e) => setCostBasis(e.target.value)} data-testid="filter-cost-basis">
                <option value="actual_vs_standard">Actual vs Standard</option>
                <option value="actual_only">Actual Only</option>
                <option value="standard_only">Standard Only</option>
              </select>
              <button type="button" className="btn btn-sm w-100 font-weight-bold"
                style={{ background: '#008080', color: '#fff', borderColor: '#008080' }}
                onClick={fetchReport} disabled={loading} data-testid="btn-apply-filter">
                <i className="fa-solid fa-filter mr-1" />{loading ? 'Loading…' : 'Apply Filters'}
              </button>
            </div>
          </div>
          <small className="text-muted">Filter by reporting period, selected brands, and recipes to refresh all analytics below.</small>
        </div>
      </div>

      {error && <div className="alert alert-danger" data-testid="report-error">{error}</div>}

      {loading && (
        <div className="text-center py-5" data-testid="report-loading">
          <div className="spinner-border" style={{ color: '#008080' }} role="status" />
        </div>
      )}

      {!loading && report && brands.length === 0 && (
        <div className="alert alert-info text-center" data-testid="report-empty">
          No completed production runs found for the selected filters.
        </div>
      )}

      {!loading && report && brands.length > 0 && (
        <>
          {/* ── KPI Cards ── */}
          <div className="row mb-4" data-testid="kpi-bar">
            {[
              { label: 'BRANDS IN SCOPE',       value: brands.length,               sub: 'Current filtered selection',       color: '#008080' },
              { label: 'PRODUCTION QUANTITY',    value: totalQty.toLocaleString(),   sub: 'Total units produced',             color: '#d97706' },
              { label: 'ACTUAL COST',            value: totalAct >= 1e6 ? `₱${(totalAct / 1e6).toFixed(2)}M` : currency(totalAct), sub: 'Across selected brands', color: '#6366f1' },
              { label: 'VARIANCE',               value: pctSigned(varPct),          sub: `${totalVar >= 0 ? 'Above' : 'Below'} standard cost baseline`, color: totalVar > 0 ? '#ef4444' : '#22c55e' },
            ].map((kpi, i) => (
              <div key={i} className="col-xl-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${kpi.color}` }}>
                  <div className="card-body py-3">
                    <div className="text-uppercase text-muted mb-2" style={{ fontSize: '0.68rem', letterSpacing: '0.08em' }}>{kpi.label}</div>
                    <div className="font-weight-bold mb-1" style={{ fontSize: '1.9rem', lineHeight: 1.1 }}>{kpi.value}</div>
                    <small className="text-muted">{kpi.sub}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Charts ── */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="row align-items-start">
                {/* Bar + Line chart */}
                <div className="col-xl-8 mb-3 mb-xl-0">
                  <h6 className="font-weight-bold mb-0">Cost Variance Overview</h6>
                  <small className="text-muted d-block mb-2">Compare actual, standard, and variance by brand.</small>

                  {/* Legend */}
                  <div className="d-flex mb-3" style={{ gap: 20 }}>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                      <div style={{ width: 14, height: 14, background: '#008080', borderRadius: 2 }} />
                      <span style={{ fontSize: '0.8rem', color: '#374151' }}>Standard Cost</span>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                      <div style={{ width: 14, height: 14, background: '#d97706', borderRadius: 2 }} />
                      <span style={{ fontSize: '0.8rem', color: '#374151' }}>Actual Cost</span>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                      <svg width="22" height="14" style={{ overflow: 'visible' }}>
                        <line x1="0" y1="7" x2="22" y2="7" stroke="#ef4444" strokeWidth="2" />
                        <circle cx="11" cy="7" r="3.5" fill="#ef4444" />
                      </svg>
                      <span style={{ fontSize: '0.8rem', color: '#374151' }}>Variance Amount</span>
                    </div>
                  </div>

                  <BarLineChart brands={brands} />
                </div>

                {/* Donut chart */}
                <div className="col-xl-4">
                  <h6 className="font-weight-bold mb-0">Variance Distribution</h6>
                  <small className="text-muted d-block mb-3">Quick view of over, under, and on-target brands.</small>
                  <DonutChart brands={brands} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Summary Table ── */}
          <div className="card border-0 shadow-sm">
            <div className="card-body border-bottom pb-3">
              <div className="d-flex align-items-start justify-content-between flex-wrap" style={{ gap: 12 }}>
                <div>
                  <h6 className="font-weight-bold mb-0">Brand Cost Allocation Summary</h6>
                  <small className="text-muted">Export-ready summary of production quantity, standard cost, actual cost, and variance by brand.</small>
                </div>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                  <select className="form-control form-control-sm" style={{ width: 'auto' }} value={varFilter}
                    onChange={(e) => { setVarFilter(e.target.value); setPage(1); }} data-testid="filter-var-state">
                    <option value="all">All Variance States</option>
                    <option value="over">Over Target</option>
                    <option value="under">Under Target</option>
                    <option value="on">On Target</option>
                  </select>
                  <div className="input-group input-group-sm" style={{ width: 200 }}>
                    <input type="text" className="form-control" placeholder="Search brand or SKU"
                      value={tableSearch} onChange={(e) => { setTableSearch(e.target.value); setPage(1); }}
                      data-testid="table-search" />
                  </div>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0" data-testid="brand-summary-table" style={{ fontSize: '0.875rem' }}>
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    {['BRAND', 'REPORTING PERIOD', 'PRODUCTION QUANTITY', 'STANDARD COST', 'ACTUAL COST', 'VARIANCE AMOUNT', 'VARIANCE %', 'ACTIONS'].map((h) => (
                      <th key={h} style={{ fontSize: '0.7rem', letterSpacing: '0.06em', color: '#6c757d', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageBrands.map((b) => (
                    <React.Fragment key={b.brand_id}>
                      <tr data-testid={`brand-row-${b.brand_id}`}>
                        <td className="font-weight-bold" data-testid={`brand-name-${b.brand_id}`}>{b.brand_name}</td>
                        <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                          {fmtDate(appliedStart)} – {fmtDate(appliedEnd)}
                        </td>
                        <td data-testid={`brand-qty-${b.brand_id}`}>{(b.production_qty || 0).toLocaleString()}</td>
                        <td data-testid={`brand-standard-${b.brand_id}`}>{currency(b.total_standard_cost)}</td>
                        <td data-testid={`brand-actual-${b.brand_id}`}>{currency(b.total_actual_cost)}</td>
                        <td data-testid={`brand-variance-${b.brand_id}`}>{currencySigned(b.total_variance)}</td>
                        <td data-testid={`brand-variance-pct-${b.brand_id}`}>
                          {variancePill(b.total_variance, b.variance_pct)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button style={LINK} onClick={() => setExpandedBrand(expandedBrand === b.brand_id ? null : b.brand_id)}
                            data-testid={`btn-breakdown-${b.brand_id}`}>
                            View Breakdown
                          </button>
                          <span className="mx-2 text-muted">|</span>
                          <button style={LINK} onClick={() => exportCsv(b)} data-testid={`btn-export-line-${b.brand_id}`}>
                            Export Line Detail
                          </button>
                        </td>
                      </tr>

                      {expandedBrand === b.brand_id && (
                        <tr data-testid={`drilldown-${b.brand_id}`}>
                          <td colSpan="8" className="p-0">
                            <div className="px-4 py-3" style={{ background: '#f8fffe', borderTop: '2px solid #008080' }}>
                              <h6 className="font-weight-bold mb-3" style={{ color: '#008080' }}>
                                <i className="fa-solid fa-chart-column mr-2" />Production Runs — {b.brand_name}
                              </h6>
                              <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-0 bg-white" data-testid="drilldown-table" style={{ fontSize: '0.8rem' }}>
                                  <thead className="thead-light">
                                    <tr>
                                      <th>Date</th><th>Item</th><th>Recipe</th>
                                      <th className="text-right">Planned</th><th className="text-right">Actual</th>
                                      <th className="text-right">Std Cost</th><th className="text-right">Actual Cost</th>
                                      <th className="text-right">Variance</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {drilldownRuns.length === 0 && (
                                      <tr><td colSpan="8" className="text-muted text-center">No completed runs in this period</td></tr>
                                    )}
                                    {drilldownRuns.map((r) => (
                                      <tr key={r.run_id} data-testid={`run-row-${r.run_id}`}>
                                        <td>{String(r.production_date).slice(0, 10)}</td>
                                        <td>{r.item_name}</td>
                                        <td>{r.version_label || '—'}</td>
                                        <td className="text-right">{r.planned_yield}</td>
                                        <td className="text-right">{r.actual_yield ?? '—'}</td>
                                        <td className="text-right">{currency(r.standard_cost)}</td>
                                        <td className="text-right">{currency(r.actual_cost)}</td>
                                        <td className="text-right">{currencySigned(r.variance)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  {/* Totals */}
                  {pageBrands.length > 0 && (
                    <tr style={{ background: '#f1f3f5', fontWeight: 700, fontSize: '0.875rem' }}>
                      <td>TOTAL</td>
                      <td />
                      <td>{totalQty.toLocaleString()}</td>
                      <td>{currency(totalStd)}</td>
                      <td>{currency(totalAct)}</td>
                      <td>{currencySigned(totalVar)}</td>
                      <td>{variancePill(totalVar, varPct)}</td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className="card-footer bg-white d-flex align-items-center justify-content-between py-2" style={{ fontSize: '0.85rem' }}>
              <span className="text-muted">
                Showing {filteredBrands.length} summary row{filteredBrands.length !== 1 ? 's' : ''} for the active filter set.
              </span>
              <div className="d-flex align-items-center" style={{ gap: 4 }}>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1} data-testid="btn-prev">Previous</button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i} className={`btn btn-sm ${page === i + 1 ? '' : 'btn-outline-secondary'}`}
                    style={page === i + 1 ? { background: '#008080', color: '#fff', borderColor: '#008080' } : {}}
                    onClick={() => setPage(i + 1)}>{i + 1}</button>
                ))}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages} data-testid="btn-next">Next</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
